import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { CiPlay1, CiPause1 } from "react-icons/ci";

// A drop-in replacement for a native <audio controls> element: it wraps a
// real (hidden) <audio> and forwards its ref, so every prop/event
// (src, <source> children, preload, onPlay, onTimeUpdate, onSeeked, etc.)
// and every existing `ref.current.play()/.pause()/.currentTime = …` call
// site keeps working exactly as before — only the visible controls change.
type AudioPlayerProps = React.AudioHTMLAttributes<HTMLAudioElement> & {
  // Picks the waveform's bar pattern (e.g. a post or contribution id), so a
  // track looks the same everywhere it's shown. Falls back to `src`.
  waveformSeed?: string;
  size?: "sm" | "lg";
};

const BAR_WIDTH = 3;
const BAR_GAP = 2;
const PEAK_RESOLUTION = 256;

const formatTime = (seconds: number) => {
  if (!isFinite(seconds) || isNaN(seconds)) return "0:00";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s < 10 ? "0" : ""}${s}`;
};

// The waveform is decorative: bar heights come from a seeded PRNG, not from
// the audio itself. Drawing the real shape would mean downloading and
// decoding every file in full (undoing preload="metadata" below, on every
// card in a list) and would need CORS headers on /media in production.
const generatePeaks = (seed: string): number[] => {
  let hash = 2166136261;
  for (let i = 0; i < seed.length; i++) {
    hash ^= seed.charCodeAt(i);
    hash = Math.imul(hash, 16777619) >>> 0;
  }
  let state = hash || 1;
  const random = () => {
    state ^= state << 13;
    state >>>= 0;
    state ^= state >>> 17;
    state ^= state << 5;
    state >>>= 0;
    return state / 4294967296;
  };

  const phase = random() * Math.PI * 2;
  const sections = 2 + Math.floor(random() * 3);
  const peaks: number[] = [];
  let level = 0.5;
  for (let i = 0; i < PEAK_RESOLUTION; i++) {
    const t = i / (PEAK_RESOLUTION - 1);
    // Fade in/out at the ends, with a few louder/quieter sections between.
    const envelope = Math.sqrt(Math.sin(Math.PI * Math.min(1, t * 1.04 + 0.02)));
    const form = 0.72 + 0.28 * Math.sin(t * Math.PI * sections * 2 + phase);
    level = level * 0.6 + random() * 0.4;
    peaks.push(Math.min(1, Math.max(0.1, (0.25 + 0.75 * level) * envelope * form)));
  }
  return peaks;
};

// Peaks are generated once at a fixed resolution and then bucketed down to
// however many bars fit, so the shape stays the same at every width.
const resamplePeaks = (peaks: number[], count: number) =>
  Array.from({ length: count }, (_, i) => {
    const start = Math.floor((i * peaks.length) / count);
    const end = Math.max(start + 1, Math.floor(((i + 1) * peaks.length) / count));
    return Math.max(...peaks.slice(start, end));
  });

const AudioPlayer = forwardRef<HTMLAudioElement, AudioPlayerProps>(
  (
    {
      className,
      children,
      controls, // intentionally dropped — this component supplies its own controls
      waveformSeed,
      size = "sm",
      onPlay,
      onPause,
      onEnded,
      onTimeUpdate,
      onSeeked,
      onLoadedMetadata,
      onDurationChange,
      ...rest
    },
    forwardedRef
  ) => {
    const innerRef = useRef<HTMLAudioElement | null>(null);
    useImperativeHandle(forwardedRef, () => innerRef.current as HTMLAudioElement);

    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);

    const waveRef = useRef<HTMLDivElement | null>(null);
    const [barCount, setBarCount] = useState(40);
    const [hoverIndex, setHoverIndex] = useState<number | null>(null);

    const seed = waveformSeed ?? (typeof rest.src === "string" ? rest.src : "");
    const peaks = useMemo(() => generatePeaks(seed), [seed]);
    const bars = useMemo(() => resamplePeaks(peaks, barCount), [peaks, barCount]);

    useEffect(() => {
      const wave = waveRef.current;
      if (!wave || typeof ResizeObserver === "undefined") return;
      const updateBarCount = () => {
        const width = wave.clientWidth;
        if (width > 0) {
          setBarCount(Math.max(12, Math.floor((width + BAR_GAP) / (BAR_WIDTH + BAR_GAP))));
        }
      };
      updateBarCount();
      const observer = new ResizeObserver(updateBarCount);
      observer.observe(wave);
      return () => observer.disconnect();
    }, []);

    const durationFixInFlightRef = useRef(false);

    const syncDuration = () => {
      const audio = innerRef.current;
      const d = audio?.duration;
      if (d !== undefined && isFinite(d)) {
        setDuration(d);
        return;
      }
      // Some files (WAV without an upfront size header, M4A that isn't
      // "faststart"-optimized, a freshly-recorded MediaRecorder clip) report
      // Infinity/NaN until the browser is forced to seek through them once.
      // Previously this branch just did nothing, so duration silently stayed
      // stuck at 0:00 forever for exactly these files. Seek near the end
      // once to force the browser to resolve the real duration, then jump
      // back to the start before the user ever sees or hears the seek.
      if (audio && !durationFixInFlightRef.current) {
        durationFixInFlightRef.current = true;
        audio.currentTime = 1e101;
        audio.addEventListener(
          "timeupdate",
          () => {
            audio.currentTime = 0;
            durationFixInFlightRef.current = false;
            if (isFinite(audio.duration)) setDuration(audio.duration);
          },
          { once: true }
        );
      }
    };

    const togglePlay = () => {
      const audio = innerRef.current;
      if (!audio) return;
      if (audio.paused) audio.play();
      else audio.pause();
    };

    const seek = (time: number) => {
      const audio = innerRef.current;
      if (!audio) return;
      audio.currentTime = time;
      setCurrentTime(time);
    };

    const pointerFraction = (e: React.PointerEvent<HTMLDivElement>) => {
      const rect = e.currentTarget.getBoundingClientRect();
      return rect.width > 0 ? Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width)) : 0;
    };

    const seekToPointer = (e: React.PointerEvent<HTMLDivElement>) => {
      if (duration > 0) seek(pointerFraction(e) * duration);
    };

    const isLarge = size === "lg";
    const progress = duration > 0 ? Math.min(currentTime / duration, 1) : 0;
    const playedBars = Math.round(progress * bars.length);

    const barColor = (index: number) => {
      if (index < playedBars) return "bg-primary";
      if (hoverIndex !== null && index <= hoverIndex) return "bg-[#8f9c8f]";
      return "bg-[#cfd5ca]";
    };

    return (
      <div
        className={`flex items-center gap-3 bg-gray-f6 ${
          isLarge ? "rounded-[22px] py-2.5 pl-2.5 pr-[18px]" : "rounded-[18px] py-2 pl-2 pr-3.5"
        } ${className ?? ""}`}
      >
        <button
          type="button"
          onClick={(e) => {
            // Players are often nested inside a clickable card/link; a play
            // click should never also trigger navigation.
            e.preventDefault();
            e.stopPropagation();
            togglePlay();
          }}
          className={`shrink-0 flex items-center justify-center ${
            isLarge ? "w-11 h-11" : "w-9 h-9"
          } rounded-full bg-primary text-white hover:bg-[#526554] transition-colors`}
          aria-label={isPlaying ? "Pause" : "Play"}
        >
          {isPlaying ? (
            <CiPause1 className={isLarge ? "text-2xl" : "text-xl"} />
          ) : (
            <CiPlay1 className={isLarge ? "text-2xl" : "text-xl"} />
          )}
        </button>

        {/* Pointer seeking is handled here rather than by the range input
            below: iOS Safari only moves a range by dragging its thumb (a tap
            on the track does nothing), and this thumb is invisible. Touch
            seeks on release so a vertical scroll that starts on the bars
            (cancelled via touch-pan-y) doesn't jump the track. */}
        <div
          ref={waveRef}
          className={`relative flex-1 min-w-0 ${isLarge ? "h-14" : "h-9"} rounded-md cursor-pointer touch-pan-y has-[:focus-visible]:outline has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-4 has-[:focus-visible]:outline-primary`}
          onPointerDown={(e) => {
            if (e.pointerType === "mouse" && e.button !== 0) return;
            e.currentTarget.setPointerCapture?.(e.pointerId);
            if (e.pointerType === "mouse") seekToPointer(e);
          }}
          onPointerMove={(e) => {
            const index = Math.floor(pointerFraction(e) * bars.length);
            setHoverIndex(Math.min(bars.length - 1, Math.max(0, index)));
            if (e.currentTarget.hasPointerCapture?.(e.pointerId)) seekToPointer(e);
          }}
          onPointerUp={(e) => {
            if (!e.currentTarget.hasPointerCapture?.(e.pointerId)) return;
            e.currentTarget.releasePointerCapture(e.pointerId);
            seekToPointer(e);
            if (e.pointerType !== "mouse") setHoverIndex(null);
          }}
          onPointerLeave={() => setHoverIndex(null)}
        >
          <div
            className="absolute inset-0 flex items-center justify-between pointer-events-none"
            aria-hidden="true"
          >
            {bars.map((peak, index) => (
              <span
                key={index}
                className={`w-[3px] shrink-0 rounded-sm transition-colors duration-100 ${barColor(index)}`}
                style={{ height: `${(peak * 100).toFixed(1)}%` }}
              />
            ))}
          </div>
          {/* Invisible range input over the bars: keeps keyboard and
              screen-reader seeking. Pointer input goes to the wrapper. */}
          <input
            type="range"
            min={0}
            max={duration || 0}
            step={0.01}
            value={Math.min(currentTime, duration || 0)}
            onChange={(e) => seek(parseFloat(e.target.value))}
            className="absolute inset-0 w-full h-full m-0 opacity-0 pointer-events-none"
            aria-label="Seek"
          />
        </div>

        <span
          className={`${isLarge ? "text-[13px]" : "text-xs"} text-gray-70 tabular-nums whitespace-nowrap shrink-0`}
        >
          {formatTime(currentTime)} / {formatTime(duration)}
        </span>

        <audio
          ref={innerRef}
          className="hidden"
          // Defaults to fetching only metadata, not the whole file. Left
          // unset, browsers disagree on what "no preload attribute" means —
          // in practice this page's project-card lists were each silently
          // pulling down every listed track in full (multi-MB apiece) just
          // to render a duration next to a play button. A caller that
          // genuinely wants eager loading (e.g. preload="auto") can still
          // override this via props, since `rest` is spread after it below.
          preload="metadata"
          onPlay={(e) => { setIsPlaying(true); onPlay?.(e); }}
          onPause={(e) => { setIsPlaying(false); onPause?.(e); }}
          onEnded={(e) => { setIsPlaying(false); onEnded?.(e); }}
          onTimeUpdate={(e) => { setCurrentTime(e.currentTarget.currentTime); onTimeUpdate?.(e); }}
          onSeeked={(e) => { setCurrentTime(e.currentTarget.currentTime); onSeeked?.(e); }}
          onLoadedMetadata={(e) => { syncDuration(); onLoadedMetadata?.(e); }}
          onDurationChange={(e) => { syncDuration(); onDurationChange?.(e); }}
          {...rest}
        >
          {children}
        </audio>
      </div>
    );
  }
);

AudioPlayer.displayName = "AudioPlayer";

export default AudioPlayer;
